import { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { FaqItem, FaqCategory, FaqListParams } from '../../types';
import { apiService } from '../../services/api';
import { CategoryManagementModal } from '../../components/CategoryManagementModal';
import { FaqReorderMode } from './FaqReorderMode';

const LIMIT = 10;

export const FaqListPage: React.FC = () => {
  const navigate = useNavigate();
  const [faqs, setFaqs] = useState<FaqItem[]>([]);
  const [categories, setCategories] = useState<FaqCategory[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [keyword, setKeyword] = useState('');
  const [filterProductCategoryId, setFilterProductCategoryId] = useState('');
  const [filterFaqCategoryId, setFilterFaqCategoryId] = useState('');
  const [inputKeyword, setInputKeyword] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [isReorderMode, setIsReorderMode] = useState(false);

  useEffect(() => {
    apiService
      .getCategories()
      .then(setCategories)
      .catch(() => {});
  }, []);

  const fetchFaqs = useCallback((params: FaqListParams) => {
    setIsLoading(true);
    setError(null);
    apiService
      .getFaqs(params)
      .then((res) => {
        setFaqs(res.faqs);
        setTotal(res.total);
      })
      .catch(() => setError('データの取得に失敗しました'))
      .finally(() => setIsLoading(false));
  }, []);

  // フィルタリング用の faqCategoryId を解決（FAQカテゴリ > 製品カテゴリ の優先順）
  const resolvedCategoryId = filterFaqCategoryId || filterProductCategoryId || undefined;

  useEffect(() => {
    fetchFaqs({
      page,
      limit: LIMIT,
      faqCategoryId: resolvedCategoryId,
      keyword: keyword || undefined,
    });
  }, [page, keyword, resolvedCategoryId, fetchFaqs]);

  const handleSearch = () => {
    setPage(1);
    setKeyword(inputKeyword);
  };

  const handleProductCategoryFilterChange = (value: string) => {
    setPage(1);
    setFilterProductCategoryId(value);
    setFilterFaqCategoryId(''); // 製品カテゴリが変わったらFAQカテゴリをリセット
  };

  const handleFaqCategoryFilterChange = (value: string) => {
    setPage(1);
    setFilterFaqCategoryId(value);
  };

  const handleDelete = async (faq: FaqItem) => {
    if (!window.confirm(`「${faq.question}」を削除してもよいですか？`)) return;
    try {
      await apiService.deleteFaq(faq.id);
      fetchFaqs({
        page,
        limit: LIMIT,
        faqCategoryId: resolvedCategoryId,
        keyword: keyword || undefined,
      });
    } catch {
      alert('削除に失敗しました');
    }
  };

  const totalPages = Math.ceil(total / LIMIT);

  const productCategories = categories.filter((c) => !c.productCategoryId);
  const faqCategories = categories.filter((c) => Boolean(c.productCategoryId));

  // 製品カテゴリフィルター選択時に表示するFAQカテゴリ一覧
  const filteredFaqCategoryOptions = filterProductCategoryId
    ? faqCategories.filter((c) => c.productCategoryId === filterProductCategoryId)
    : faqCategories;

  const getProductCategoryName = (childCategoryId: string) => {
    const child = categories.find((c) => c.id === childCategoryId);
    if (!child?.productCategoryId) return '—';
    return categories.find((c) => c.id === child.productCategoryId)?.name ?? '—';
  };

  const getFaqCategoryName = (childCategoryId: string) =>
    categories.find((c) => c.id === childCategoryId)?.name ?? childCategoryId;

  const isFaqCategorySelected = Boolean(filterFaqCategoryId);
  const selectedFaqCategory = categories.find((c) => c.id === filterFaqCategoryId);

  if (isReorderMode && selectedFaqCategory) {
    return (
      <FaqReorderMode
        categoryId={selectedFaqCategory.id}
        categoryName={selectedFaqCategory.name}
        onClose={() => setIsReorderMode(false)}
        onSaved={() => {
          setIsReorderMode(false);
          fetchFaqs({
            page,
            limit: LIMIT,
            faqCategoryId: resolvedCategoryId,
            keyword: keyword || undefined,
          });
        }}
      />
    );
  }

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-900">FAQ管理</h1>
        <div className="flex gap-2">
          <button
            onClick={() => setIsReorderMode(true)}
            disabled={!isFaqCategorySelected}
            title={isFaqCategorySelected ? '' : 'FAQカテゴリを選択してください'}
            className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            並び替え
          </button>
          <button
            onClick={() => setIsCategoryModalOpen(true)}
            className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            カテゴリ管理
          </button>
          <button
            onClick={() => navigate('/faq/new')}
            className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
          >
            + 新規追加
          </button>
        </div>
      </div>

      {/* フィルターバー */}
      <div className="mb-4 flex flex-wrap gap-3">
        {/* キーワード */}
        <div className="flex flex-1 gap-2">
          <input
            type="text"
            placeholder="キーワード検索..."
            value={inputKeyword}
            onChange={(e) => setInputKeyword(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            className="min-w-0 flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <button
            onClick={handleSearch}
            className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            検索
          </button>
        </div>

        {/* 製品カテゴリフィルター */}
        <select
          value={filterProductCategoryId}
          onChange={(e) => handleProductCategoryFilterChange(e.target.value)}
          className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">すべての製品カテゴリ</option>
          {productCategories.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>

        {/* FAQカテゴリフィルター */}
        <select
          value={filterFaqCategoryId}
          onChange={(e) => handleFaqCategoryFilterChange(e.target.value)}
          disabled={filteredFaqCategoryOptions.length === 0}
          className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <option value="">すべてのFAQカテゴリ</option>
          {filteredFaqCategoryOptions.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </div>

      {error && <div className="mb-4 rounded-lg bg-red-50 p-4 text-red-700">{error}</div>}

      {/* テーブル */}
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <table className="w-full">
          <thead className="bg-slate-50">
            <tr>
              <th className="w-12 px-4 py-3 text-left text-sm font-medium text-slate-500">No.</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-slate-500">質問</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-slate-500">回答</th>
              <th className="w-28 px-4 py-3 text-left text-sm font-medium text-slate-500">
                製品カテゴリ
              </th>
              <th className="w-36 px-4 py-3 text-left text-sm font-medium text-slate-500">
                FAQカテゴリ
              </th>
              <th className="w-24 px-4 py-3 text-center text-sm font-medium text-slate-500">
                操作
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {isLoading ? (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-sm text-slate-500">
                  読み込み中...
                </td>
              </tr>
            ) : faqs.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-sm text-slate-500">
                  FAQ がありません
                </td>
              </tr>
            ) : (
              faqs.map((faq, idx) => (
                <tr key={faq.id} className="hover:bg-slate-50">
                  <td className="px-4 py-4 text-sm text-slate-400">
                    {(page - 1) * LIMIT + idx + 1}
                  </td>
                  <td className="px-4 py-4 text-sm font-medium text-slate-900">
                    <Link
                      to={`/faq/${faq.id}/edit`}
                      className="line-clamp-2 hover:text-blue-600 hover:underline"
                    >
                      {faq.question}
                    </Link>
                  </td>
                  <td className="px-4 py-4 text-sm text-slate-600">
                    <span className="line-clamp-2">
                      {faq.answer.length > 60 ? `${faq.answer.slice(0, 60)}...` : faq.answer}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    <span className="inline-block rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700">
                      {getProductCategoryName(faq.faqCategoryId)}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    <span className="inline-block rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">
                      {getFaqCategoryName(faq.faqCategoryId)}
                    </span>
                  </td>
                  <td className="px-4 py-4 text-center">
                    <div className="flex items-center justify-center gap-2">
                      <Link
                        to={`/faq/${faq.id}/edit`}
                        className="rounded px-2 py-1 text-xs font-medium text-blue-600 hover:bg-blue-50"
                      >
                        編集
                      </Link>
                      <button
                        onClick={() => handleDelete(faq)}
                        className="rounded px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50"
                      >
                        削除
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* ページネーション */}
      {!isLoading && total > 0 && (
        <div className="mt-4 flex items-center justify-between">
          <span className="text-sm text-slate-600">
            全 {total} 件 / {page} / {totalPages} ページ
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => setPage((p) => p - 1)}
              disabled={page === 1}
              className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              前へ
            </button>
            <button
              onClick={() => setPage((p) => p + 1)}
              disabled={page >= totalPages}
              className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              次へ
            </button>
          </div>
        </div>
      )}

      <CategoryManagementModal
        isOpen={isCategoryModalOpen}
        onClose={() => setIsCategoryModalOpen(false)}
        categories={categories}
        onCategoriesChange={() => {
          apiService
            .getCategories()
            .then(setCategories)
            .catch((err) => console.error('Failed to refresh categories:', err));
        }}
      />
    </div>
  );
};
